// Single source of truth for tournaments: the live game's PUBLIC API
// Links point at /t/<slug>, the PUBLIC event landing (register + seat-claim
// flow). /tournaments/<id> is the logged-in bracket page and bounces
// logged-out visitors to a login wall.
// (play.traderoyale.com). The operator manages tournaments only in the game
// admin; this marketing page reflects them automatically. No Sanity duplication.
// Fetched server-side (no CORS), ISR-cached, and fails safe to an empty list so
// the page degrades to the honest "incoming" state if the game is unreachable.

export const GAME_URL = "https://play.traderoyale.com";
const GAME_API = `${GAME_URL}/api/tournaments`;

type GameTournament = {
  id: number;
  name: string;
  slug: string;
  status: string; // REGISTRATION | LIVE | COMPLETED | CANCELLED
  format?: string;
  startTime?: string | null;
};

export type DisplayStatus = "Upcoming" | "Live" | "Replay";

export type DisplayTournament = {
  id: number;
  name: string;
  slug: string;
  status: DisplayStatus;
  startTime: string | null;
  url: string;
};

const STATUS_MAP: Record<string, DisplayStatus> = {
  REGISTRATION: "Upcoming",
  LIVE: "Live",
  COMPLETED: "Replay",
  // CANCELLED and anything unknown are intentionally dropped.
};

const ORDER: Record<DisplayStatus, number> = { Live: 0, Upcoming: 1, Replay: 2 };

export async function getGameTournaments(): Promise<DisplayTournament[]> {
  try {
    const res = await fetch(GAME_API, { next: { revalidate: 60 } });
    if (!res.ok) return [];
    const raw = (await res.json()) as GameTournament[];
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((t) => t && STATUS_MAP[t.status])
      .map((t) => ({
        id: t.id,
        name: (t.name || "").trim(),
        slug: t.slug,
        status: STATUS_MAP[t.status],
        startTime: t.startTime ?? null,
        url: `${GAME_URL}/t/${t.slug}`,
      }))
      .sort((a, b) => {
        if (ORDER[a.status] !== ORDER[b.status]) return ORDER[a.status] - ORDER[b.status];
        const at = a.startTime ? new Date(a.startTime).getTime() : Infinity;
        const bt = b.startTime ? new Date(b.startTime).getTime() : Infinity;
        return at - bt;
      });
  } catch {
    return [];
  }
}

// Sprint 9 — the next dated upcoming event, used to drive the tournaments
// hero (name, countdown, enter CTA) from the game instead of Sanity. The
// list is already sorted Live → Upcoming by soonest start, so first match
// wins. Live events also qualify: "enter/watch now" beats a countdown.
export function getNextEvent(
  tournaments: DisplayTournament[],
): DisplayTournament | null {
  return (
    tournaments.find(
      (t) => t.status === "Live" || (t.status === "Upcoming" && t.startTime),
    ) ?? null
  );
}
